"use client";

import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { ArrowRight } from "lucide-react";
export default function Bedrock() {
  const eyebrowReveal = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const hairlineReveal = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const headlineReveal = useRevealOnScroll<HTMLHeadingElement>({
    threshold: 0.15
  });
  const para1Reveal = useRevealOnScroll<HTMLParagraphElement>({
    threshold: 0.15
  });
  const para2Reveal = useRevealOnScroll<HTMLParagraphElement>({
    threshold: 0.15
  });
  const linkReveal = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const imageReveal = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const captionReveal = useRevealOnScroll<HTMLParagraphElement>({
    threshold: 0.15
  });
  return <section data-section-id="20023" id="the-bedrock-standard" className="bg-background py-32 md:py-44">
      <div className="mx-auto w-full max-w-[1280px] px-5 sm:px-8 md:px-10">
        <div className="grid grid-cols-1 gap-12 md:grid-cols-12 md:gap-7">
          {}
          <div className="order-2 md:order-1 md:col-span-7">
            {}
            <div ref={eyebrowReveal.ref} className="transition-all duration-700 ease-out" style={{
            opacity: eyebrowReveal.isVisible ? 1 : 0,
            transform: eyebrowReveal.isVisible ? "translateY(0)" : "translateY(16px)"
          }}>
              <span className="font-default text-[0.75rem] font-bold uppercase leading-none tracking-[0.15em] text-primary">
                Engineering Integrity
              </span>
            </div>

            {}
            <div ref={hairlineReveal.ref} className="mt-6 transition-all duration-700 ease-out" style={{
            opacity: hairlineReveal.isVisible ? 1 : 0,
            transform: hairlineReveal.isVisible ? "translateY(0)" : "translateY(16px)",
            transitionDelay: "80ms"
          }}>
              <div className="h-px w-14 bg-border" />
            </div>

            {}
            <h1 ref={headlineReveal.ref} className="mt-10 font-serif font-semibold text-foreground transition-all duration-700 ease-out" style={{
            fontSize: "clamp(2rem, 3.5vw, 3.5rem)",
            letterSpacing: "-0.02em",
            lineHeight: "1.02",
            opacity: headlineReveal.isVisible ? 1 : 0,
            transform: headlineReveal.isVisible ? "translateY(0)" : "translateY(16px)",
            transitionDelay: "160ms"
          }}>
              From a single technical mandate, to engineered safety systems.
            </h1>

            {}
            <div className="mt-10 max-w-[580px] space-y-6">
              <p ref={para1Reveal.ref} className="font-default font-normal text-foreground transition-all duration-700 ease-out" style={{
              fontSize: "clamp(1.0625rem, 1.15vw, 1.1875rem)",
              lineHeight: "1.6",
              opacity: para1Reveal.isVisible ? 1 : 0,
              transform: para1Reveal.isVisible ? "translateY(0)" : "translateY(16px)",
              transitionDelay: "240ms"
            }}>
                Bedrock Safety Engineering was founded on the principle that fire protection is a rigorous engineering discipline, not a compliance checkbox. We began by analyzing the complex risks of hazardous manufacturing environments, ensuring that every safety system is as robust as the infrastructure it protects.
              </p>
              <p ref={para2Reveal.ref} className="font-default font-normal text-foreground transition-all duration-700 ease-out" style={{
              fontSize: "clamp(1.0625rem, 1.15vw, 1.1875rem)",
              lineHeight: "1.6",
              opacity: para2Reveal.isVisible ? 1 : 0,
              transform: para2Reveal.isVisible ? "translateY(0)" : "translateY(16px)",
              transitionDelay: "320ms"
            }}>
                Our methodology integrates advanced risk modeling with precise fire protection design. From aerospace facilities to pharmaceutical plants, we provide the technical clarity required by underwriters and operators alike, ensuring that your industrial assets remain protected against the most consequential threats.
              </p>
            </div>

            {}
            <div ref={linkReveal.ref} className="mt-12 transition-all duration-700 ease-out" style={{
            opacity: linkReveal.isVisible ? 1 : 0,
            transform: linkReveal.isVisible ? "translateY(0)" : "translateY(16px)",
            transitionDelay: "400ms"
          }}>
              <Link to="/AboutPage" className="group inline-flex items-center gap-2 font-default text-[0.9375rem] font-bold text-primary">
                <span>View our technical approach</span>
                <ArrowRight className="size-3 transition-transform duration-200 ease-out group-hover:translate-x-1" strokeWidth={2} />
              </Link>
            </div>
          </div>

          {}
          <div className="order-1 md:order-2 md:col-span-5">
            <div ref={imageReveal.ref} className="transition-opacity duration-700 ease-out" style={{
            opacity: imageReveal.isVisible ? 1 : 0
          }}>
              <div className="border border-border">
                <div className="aspect-[16/10] w-full overflow-hidden md:aspect-[4/5]">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1705042/img/homepage-image.webp" alt="Inspection report and technical analysis of fire protection systems in a high-hazard facility" loading="lazy" decoding="async" className="h-full w-full object-cover" />
                </div>
              </div>
              <p ref={captionReveal.ref} className="mt-4 font-default text-[0.8125rem] font-medium leading-[1.45] tracking-[0.01em] text-muted-foreground transition-all duration-700 ease-out" style={{
              opacity: captionReveal.isVisible ? 1 : 0,
              transform: captionReveal.isVisible ? "translateY(0)" : "translateY(16px)",
              transitionDelay: "240ms"
            }}>
                Inspection report of fire protection systems in a high-hazard facility.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>;
}